import ConfirmDialog from '../components/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import Layout from '../components/Layout';

const API = 'https://api.safecorepest.com/api';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '', epa_number: '', active_ingredient: '', ai_percentage: '',
    target_pests: '', category: 'insecticide', is_billable: false, unit_price: ''
  });

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Products might not have a dedicated route yet, use direct query approach
      // For now fetch from the products endpoint if it exists
      const prodRes = await fetch(`${API}/facilities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // We will need to create a products endpoint - for now use hardcoded fetch
    } catch (err) {
      console.error('Error:', err);
    }
    // Direct fetch products
    try {
      const res = await fetch(`${API}/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || data || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProduct ? `${API}/products/${editingProduct.id}` : `${API}/products`;
      const method = editingProduct ? 'PUT' : 'POST';
      const payload = { ...formData, company_id: user.company_id };
      if (payload.ai_percentage === '') delete payload.ai_percentage;
      else payload.ai_percentage = parseFloat(payload.ai_percentage);
      if (payload.unit_price === '' || !payload.is_billable) payload.unit_price = 0;
      else payload.unit_price = parseFloat(payload.unit_price);

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowModal(false);
        setEditingProduct(null);
        resetForm();
        fetchProducts();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Error saving product');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleEdit = (prod) => {
    setEditingProduct(prod);
    setFormData({
      name: prod.name, epa_number: prod.epa_number || '',
      active_ingredient: prod.active_ingredient || '',
      ai_percentage: prod.ai_percentage || '',
      target_pests: prod.target_pests || '', category: prod.category || 'insecticide',
      is_billable: prod.is_billable || false, unit_price: prod.unit_price || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (prod) => {
    setConfirmDialog({ message: `Delete "${prod.name}"? This cannot be undone.`, confirmText: "Delete Product", icon: "🗑", onConfirm: async () => {
      setConfirmDialog(null);
    try {
      await fetch(`${API}/products/${prod.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    }});
  };

  const resetForm = () => {
    setFormData({
      name: '', epa_number: '', active_ingredient: '', ai_percentage: '',
      target_pests: '', category: 'insecticide', is_billable: false, unit_price: ''
    });
  };

  const openNewModal = () => {
    setEditingProduct(null);
    resetForm();
    setShowModal(true);
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'rodenticide', label: 'Rodenticides' },
    { value: 'insecticide', label: 'Insecticides' },
    { value: 'glue_board', label: 'Glue Boards' },
    { value: 'trap', label: 'Traps' },
    { value: 'dust', label: 'Dusts' },
    { value: 'granular', label: 'Granulars' },
    { value: 'bait', label: 'Baits' },
    { value: 'spray', label: 'Sprays' },
    { value: 'other', label: 'Other' },
  ];

  const categoryColors = {
    rodenticide: { bg: '#FEE2E2', color: '#991B1B' },
    insecticide: { bg: '#DBEAFE', color: '#1E40AF' },
    glue_board: { bg: '#FEF3C7', color: '#92400E' },
    trap: { bg: '#E0E7FF', color: '#3730A3' },
    dust: { bg: '#F3E8FF', color: '#6B21A8' },
    granular: { bg: '#D1FAE5', color: '#065F46' },
    bait: { bg: '#FFE4E6', color: '#9F1239' },
    spray: { bg: '#CCFBF1', color: '#115E59' },
    other: { bg: '#F3F4F6', color: '#374151' },
  };

  const filtered = products.filter(p => {
    const matchCat = filterCategory === 'all' || p.category === filterCategory;
    const matchSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.epa_number && p.epa_number.includes(searchTerm));
    return matchCat && matchSearch;
  });

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.pageTitle}>Product Catalog</h2>
            <p style={styles.pageSubtitle}>{products.length} products registered</p>
          </div>
          <button onClick={openNewModal} style={styles.addBtn}>+ Add Product</button>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <input
            style={styles.searchInput}
            placeholder="Search by name or EPA #..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <select style={styles.filterSelect} value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}>
            {categories.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Product Name</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>EPA Registration #</th>
                  <th style={styles.th}>Active Ingredient</th>
                  <th style={styles.th}>AI %</th>
                  <th style={styles.th}>Target Pests</th>
                  <th style={{...styles.th, textAlign:'center'}}>Billable</th>
                  <th style={{...styles.th, textAlign:'right'}}>Unit Price</th>
                  <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((prod) => {
                  const cc = categoryColors[prod.category] || categoryColors.other;
                  return (
                    <tr key={prod.id} style={styles.tr}>
                      <td style={{...styles.td, fontWeight: '500'}}>{prod.name}</td>
                      <td style={styles.td}>
                        <span style={{...styles.badge, backgroundColor: cc.bg, color: cc.color}}>
                          {prod.category?.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={styles.td}>{prod.epa_number || '—'}</td>
                      <td style={styles.td}>{prod.active_ingredient || '—'}</td>
                      <td style={styles.td}>{prod.ai_percentage ? `${prod.ai_percentage}%` : '—'}</td>
                      <td style={{...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {prod.target_pests || '—'}
                      </td>
                      <td style={{...styles.td, textAlign:'center'}}>
                        {prod.is_billable
                          ? <span style={{background:'#D1FAE5',color:'#065F46',padding:'3px 10px',borderRadius:'12px',fontSize:'12px',fontWeight:600}}>Yes</span>
                          : <span style={{background:'#F3F4F6',color:'#9CA3AF',padding:'3px 10px',borderRadius:'12px',fontSize:'12px',fontWeight:600}}>No</span>}
                      </td>
                      <td style={{...styles.td, textAlign:'right', fontWeight: prod.is_billable ? 600 : 400, color: prod.is_billable ? '#1A3A2A' : '#9CA3AF'}}>
                        {prod.is_billable && prod.unit_price > 0 ? '$' + parseFloat(prod.unit_price).toFixed(2) : '—'}
                      </td>
                      <td style={{...styles.td, textAlign: 'center'}}>
                        <button onClick={() => handleEdit(prod)} style={styles.actionBtn}>Edit</button>
                        <button onClick={() => handleDelete(prod)} style={{...styles.actionBtn, color: '#DC2626'}}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan="9" style={{...styles.td, textAlign: 'center', padding: '40px', color: '#9CA3AF'}}>
                    No products found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div style={styles.overlay} onClick={() => setShowModal(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                <button onClick={() => setShowModal(false)} style={styles.closeBtn}>✕</button>
              </div>
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Product Name *</label>
                  <input style={styles.input} required value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})} />
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Category *</label>
                    <select style={styles.input} value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}>
                      {categories.filter(c => c.value !== 'all').map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>EPA Registration #</label>
                    <input style={styles.input} value={formData.epa_number}
                      placeholder="e.g., 12455-79"
                      onChange={e => setFormData({...formData, epa_number: e.target.value})} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Active Ingredient</label>
                    <input style={styles.input} value={formData.active_ingredient}
                      placeholder="e.g., Bromadiolone"
                      onChange={e => setFormData({...formData, active_ingredient: e.target.value})} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>AI Percentage (%)</label>
                    <input style={styles.input} type="number" step="0.0001" value={formData.ai_percentage}
                      placeholder="e.g., 0.005"
                      onChange={e => setFormData({...formData, ai_percentage: e.target.value})} />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Target Pests</label>
                  <input style={styles.input} value={formData.target_pests}
                    placeholder="e.g., Norway Rat, Roof Rat, House Mouse"
                    onChange={e => setFormData({...formData, target_pests: e.target.value})} />
                </div>
                <div style={{...styles.formGroup, background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'8px', padding:'14px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom: formData.is_billable ? '12px' : '0'}}>
                    <input type="checkbox" id="is_billable" checked={formData.is_billable}
                      onChange={e => setFormData({...formData, is_billable: e.target.checked, unit_price: e.target.checked ? formData.unit_price : ''})}
                      style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#16A34A'}} />
                    <label htmlFor="is_billable" style={{...styles.label, margin:0, color:'#15803D', cursor:'pointer'}}>
                      Billable to Client — this product/device is charged separately
                    </label>
                  </div>
                  {formData.is_billable && (
                    <div style={{marginTop:'8px'}}>
                      <label style={styles.label}>Unit Price ($) *</label>
                      <input style={{...styles.input, maxWidth:'180px'}} type="number" step="0.01" min="0"
                        placeholder="e.g., 15.00" value={formData.unit_price}
                        onChange={e => setFormData({...formData, unit_price: e.target.value})} />
                      <p style={{margin:'6px 0 0', fontSize:'12px', color:'#6B7280'}}>Price charged per unit when installed or applied during service</p>
                    </div>
                  )}
                </div>
                <div style={styles.formActions}>
                  <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>Cancel</button>
                  <button type="submit" style={styles.submitBtn}>
                    {editingProduct ? 'Update' : 'Add'} Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor='#DC2626'
          icon={confirmDialog.icon || '⚠'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </Layout>

  );
};

const styles = {
  page: { maxWidth: '1200px', margin: '0 auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  pageTitle: { margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageSubtitle: { margin: '4px 0 0', fontSize: '14px', color: '#6B7280' },
  addBtn: {
    backgroundColor: '#2D5C3F', color: '#FFFFFF', border: 'none', padding: '10px 20px',
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  },
  filters: { display: 'flex', gap: '12px', marginBottom: '20px' },
  searchInput: {
    flex: 1, padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', outline: 'none',
  },
  filterSelect: {
    padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', outline: 'none', backgroundColor: '#FFFFFF', minWidth: '180px',
  },
  loading: { textAlign: 'center', padding: '60px', color: '#6B7280', fontSize: '16px' },
  tableContainer: {
    backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB',
    overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6B7280', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  tr: { borderBottom: '1px solid #F3F4F6' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151' },
  badge: {
    padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
    display: 'inline-block', textTransform: 'capitalize',
  },
  actionBtn: {
    background: 'none', border: 'none', color: '#2D5C3F', cursor: 'pointer',
    fontWeight: '500', fontSize: '13px', padding: '4px 8px',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF', borderRadius: '12px', width: '560px',
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
  },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
    color: '#6B7280', padding: '4px',
  },
  form: { padding: '24px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '0' },
  formGroup: { flex: 1, marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' },
  cancelBtn: {
    padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '8px',
    backgroundColor: '#FFFFFF', cursor: 'pointer', fontWeight: '500', fontSize: '14px', color: '#374151',
  },
  submitBtn: {
    padding: '10px 24px', border: 'none', borderRadius: '8px',
    backgroundColor: '#2D5C3F', color: '#FFFFFF', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  },
};

export default Products;
